import { useParams } from "react-router-dom";
import {
  useBundle,
  teamName,
  slotLabel,
  bracketGamesById,
  bundleViewState,
} from "../../lib/bundle";
import { gameDay, gameLocation, gameTime } from "../../lib/format";
import { isGameLive, useNow } from "../../lib/liveStatus";
import type { Bundle, Game } from "../../lib/types";
import type { ResolvedBracketGame } from "@alpha/engine";
import { DivisionPageHeader } from "../../components/DivisionTabs";
import {
  ArrowUpRightIcon,
  CalendarIcon,
  Card,
  EmptyState,
  GameStatusBadge,
  LoadingBlock,
  MapPinIcon,
  stagger,
  cx,
} from "../../components/ui";

const venueLabel = (bundle: Bundle, g: Game) => gameLocation(bundle.venues, g);

const dateKey = (bundle: Bundle, iso?: string): string =>
  gameDay(iso, bundle.tournament.timezone) || "Unscheduled";

const timeLabel = (bundle: Bundle, iso?: string): string =>
  gameTime(iso, bundle.tournament.timezone);

/**
 * Public schedule grouped by date (spec §10). Refreshed by the bundle poll, and
 * separately by a clock tick, which decides which game reads as being played
 * right now.
 */
export function Schedule() {
  const { tid, did } = useParams();
  const q = useBundle(tid);
  // Above the early returns below: a hook down beside the derived values would
  // be conditional. One interval for the page, not one per card.
  const now = useNow();
  const bundle = q.data;

  const view = bundleViewState(q);
  if (view === "notFound")
    return <EmptyState title="Tournament not found" className="mx-auto max-w-md" />;
  if (view === "error")
    return (
      <EmptyState
        title="Couldn't load this tournament"
        hint="Check your connection and try again."
        className="mx-auto max-w-md"
      />
    );
  if (view === "loading" || !bundle) return <LoadingBlock />;
  const division = bundle.divisions.find((d) => d.id === did);
  if (!division) return <EmptyState title="Division not found" className="mx-auto max-w-md" />;

  const games = bundle.games
    .filter((g) => g.divisionId === division.id)
    // Ordinary comparison, NOT localeCompare. The "~" sentinel only sorts after
    // an ISO date in codepoint order; localeCompare's default collation weighs
    // punctuation BELOW digits, so it put unscheduled games first instead of
    // last. Verified: "~".localeCompare("2026") === -1, while "~" > "2026".
    .sort((a, b) => {
      const av = a.scheduledAt ?? "~";
      const bv = b.scheduledAt ?? "~";
      return av === bv ? 0 : av < bv ? -1 : 1;
    });

  // A bracket game carries no home/away team: who plays it comes from the
  // division's locked seeds and from the winners of the games feeding it. The
  // bracket page resolves that; without this the schedule beside it would list
  // the same three games as "TBD vs TBD". Resolved once here rather than per
  // card, which would re-walk the bracket for every game on every render — and
  // renders now come from the clock as well as the poll.
  const bracketByGame = bracketGamesById(bundle, division.id);

  const byDate = new Map<string, Game[]>();
  for (const g of games) {
    const k = dateKey(bundle, g.scheduledAt);
    const list = byDate.get(k) ?? [];
    list.push(g);
    byDate.set(k, list);
  }

  return (
    <div className="space-y-8">
      <DivisionPageHeader bundle={bundle} division={division} />

      {byDate.size === 0 ? (
        <EmptyState
          icon={<CalendarIcon className="h-5 w-5" />}
          title="No games scheduled yet"
          hint="Check back once the schedule is posted."
        />
      ) : null}

      {[...byDate.entries()].map(([date, dayGames], di) => (
        <section key={date} className="animate-rise space-y-3" style={stagger(di + 1)}>
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-400">
            <CalendarIcon className="h-4 w-4" />
            {date}
          </h2>
          {/*
            A game card is a fixed unit — a time rail, two team lines, a venue —
            so a day's games tile rather than stack. Saturday at a four-field
            park is twenty games: one column means scrolling past most of them,
            three columns puts the whole day on one screen.
          */}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {dayGames.map((g) => (
              <GameCard
                key={g.id}
                bundle={bundle}
                game={g}
                now={now}
                resolved={bracketByGame.get(g.id)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/** `resolved` is set only for a bracket game, whose sides are derived. */
function GameCard({
  bundle,
  game: g,
  now,
  resolved,
}: {
  bundle: Bundle;
  game: Game;
  now: number;
  resolved?: ResolvedBracketGame;
}) {
  const loc = venueLabel(bundle, g);
  const done = g.status === "final" || g.status === "forfeit";
  const showScore = done && g.homeScore !== undefined && g.awayScore !== undefined;
  const homeWin = showScore && g.homeScore! > g.awayScore!;
  const awayWin = showScore && g.awayScore! > g.homeScore!;
  const canceled = g.status === "canceled";

  return (
    <Card className={cx("flex overflow-hidden", canceled && "opacity-60")}>
      <div className="flex w-20 shrink-0 flex-col items-center justify-center gap-1.5 border-r border-slate-100 bg-slate-50/70 px-2 py-4 sm:w-24">
        <span className="text-sm font-semibold text-navy-950">
          {timeLabel(bundle, g.scheduledAt) || "TBD"}
        </span>
        <GameStatusBadge
          status={g.status}
          live={isGameLive(g, bundle.tournament.config, now)}
          hideScheduled
        />
      </div>
      <div className="min-w-0 flex-1 space-y-1 px-4 py-3">
        <TeamLine
          name={resolved ? slotLabel(bundle, resolved.home) : teamName(bundle, g.homeTeamId)}
          pending={resolved ? resolved.home.teamId === null : !g.homeTeamId}
          score={showScore ? g.homeScore : undefined}
          win={homeWin}
          struck={canceled}
        />
        <TeamLine
          name={resolved ? slotLabel(bundle, resolved.away) : teamName(bundle, g.awayTeamId)}
          pending={resolved ? resolved.away.teamId === null : !g.awayTeamId}
          score={showScore ? g.awayScore : undefined}
          win={awayWin}
          struck={canceled}
        />
        {loc ? (
          <p className="pt-1 text-xs text-slate-400">
            {loc.href ? (
              <a
                href={loc.href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 transition-colors hover:text-brandText"
              >
                <MapPinIcon className="h-3.5 w-3.5" />
                {loc.text}
                <ArrowUpRightIcon className="h-3 w-3" />
              </a>
            ) : (
              <span className="inline-flex items-center gap-1">
                <MapPinIcon className="h-3.5 w-3.5" />
                {loc.text}
              </span>
            )}
          </p>
        ) : null}
      </div>
    </Card>
  );
}

function TeamLine({
  name,
  pending,
  score,
  win,
  struck,
}: {
  name: string;
  /** No team yet — "TBD", "Seed 2", "Winner Gm 1". Set in italics, as on the bracket. */
  pending: boolean;
  score?: number;
  win: boolean;
  struck: boolean;
}) {
  return (
    <p
      className={cx(
        "flex items-baseline justify-between gap-3 text-sm",
        // Mutually exclusive, as on the bracket's own slot rows, rather than
        // emitting two text colors and leaning on twMerge to drop one.
        win ? "font-semibold text-navy-950" : pending ? "italic text-slate-400" : "text-slate-600",
        struck && "line-through",
      )}
    >
      <span className="truncate">{name}</span>
      {score !== undefined ? (
        <span
          className={cx(
            "tabular-nums text-base font-semibold",
            win ? "text-brandText" : "text-slate-400",
          )}
        >
          {score}
        </span>
      ) : null}
    </p>
  );
}
